import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Sparkles, Mail, Calendar, Download, Trash2, RefreshCw,
  Send, Gift, TestTube2, Users, TrendingUp, Award
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminWaitlist() {
  const [tab, setTab] = useState('list'); // 'list' | 'blast' | 'bonus'
  const [entries, setEntries] = useState([]);
  const [threshold, setThreshold] = useState(3);
  const [loading, setLoading] = useState(true);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/waitlist`, { headers: authHeaders() });
      setEntries(data.entries || []);
      setThreshold(data.referral_threshold || 3);
    } catch (err) {
      toast.error('Failed to load waitlist');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchEntries(); }, []);

  const stats = {
    total: entries.length,
    referrers: entries.filter(e => (e.referral_count || 0) > 0).length,
    unlocked: entries.filter(e => e.bonus_unlocked).length,
  };

  return (
    <div className="space-y-4" data-testid="admin-waitlist">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-2">
        <h2 className="text-xl font-bold flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-cyan-400" /> First-Listen Waitlist
        </h2>
        <div className="flex gap-2">
          <button
            onClick={() => setTab('list')}
            className={`px-3 py-2 rounded-md text-sm ${tab === 'list' ? 'bg-cyan-500/20 text-cyan-300' : 'text-gray-400 hover:text-white'}`}
            data-testid="waitlist-subtab-list"
          >
            <Users className="w-4 h-4 inline mr-1" /> Fans
          </button>
          <button
            onClick={() => setTab('blast')}
            className={`px-3 py-2 rounded-md text-sm ${tab === 'blast' ? 'bg-purple-500/20 text-purple-300' : 'text-gray-400 hover:text-white'}`}
            data-testid="waitlist-subtab-blast"
          >
            <Send className="w-4 h-4 inline mr-1" /> Blast
          </button>
          <button
            onClick={() => setTab('bonus')}
            className={`px-3 py-2 rounded-md text-sm ${tab === 'bonus' ? 'bg-pink-500/20 text-pink-300' : 'text-gray-400 hover:text-white'}`}
            data-testid="waitlist-subtab-bonus"
          >
            <Gift className="w-4 h-4 inline mr-1" /> Referral Reward
          </button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard icon={<Users className="w-4 h-4" />} label="Total" value={stats.total} color="cyan" />
        <StatCard icon={<TrendingUp className="w-4 h-4" />} label="Referrers" value={stats.referrers} color="purple" />
        <StatCard icon={<Award className="w-4 h-4" />} label="Unlocked" value={stats.unlocked} color="pink" />
      </div>

      {tab === 'list' && (
        <FansTab
          entries={entries}
          loading={loading}
          threshold={threshold}
          onRefresh={fetchEntries}
          authHeaders={authHeaders}
        />
      )}
      {tab === 'blast' && <BlastTab count={stats.total} authHeaders={authHeaders} />}
      {tab === 'bonus' && <BonusTab threshold={threshold} authHeaders={authHeaders} />}
    </div>
  );
}

// ---------- Stat Card ----------
const StatCard = ({ icon, label, value, color }) => {
  const colorMap = {
    cyan: 'border-cyan-500/30 text-cyan-300',
    purple: 'border-purple-500/30 text-purple-300',
    pink: 'border-pink-500/30 text-pink-300',
  };
  return (
    <div className={`glass-card p-4 border ${colorMap[color]}`}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
        {icon} {label}
      </div>
      <div className="text-2xl font-bold text-white mt-1">{value}</div>
    </div>
  );
};

// ---------- Fans Tab ----------
const FansTab = ({ entries, loading, threshold, onRefresh, authHeaders }) => {
  const handleDelete = async (id, email) => {
    if (!window.confirm(`Remove ${email} from the waitlist?`)) return;
    try {
      await axios.delete(`${API_URL}/admin/waitlist/${id}`, { headers: authHeaders() });
      toast.success('Removed');
      onRefresh();
    } catch {
      toast.error('Failed to remove');
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/waitlist/export`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <button
          onClick={onRefresh}
          className="btn-ghost flex items-center gap-2 text-sm"
          data-testid="waitlist-refresh-button"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
        <button
          onClick={handleExport}
          disabled={entries.length === 0}
          className="btn-primary flex items-center gap-2 text-sm disabled:opacity-50"
          data-testid="waitlist-export-button"
        >
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {loading ? (
        <div className="glass-card p-8 text-center text-gray-400">Loading...</div>
      ) : entries.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Sparkles className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No signups yet</h3>
          <p className="text-gray-400">Fans who join from the homepage will show up here.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.id}
              className="glass-card p-4 flex items-center gap-4"
              data-testid={`waitlist-entry-${entry.email}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                <span className="text-white font-bold">
                  {(entry.name || entry.email || '?')[0].toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h4 className="font-medium truncate">{entry.name || 'Anonymous fan'}</h4>
                  {entry.bonus_unlocked && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] bg-pink-500/20 text-pink-300 border border-pink-500/30 uppercase tracking-wider font-semibold">
                      Unlocked
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-400 flex items-center gap-1 truncate">
                  <Mail className="w-3 h-3 flex-shrink-0" /> {entry.email}
                </p>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  ref: <span className="font-mono text-cyan-400">{entry.referral_code}</span>
                  {entry.referred_by && (
                    <span className="ml-3">via <span className="font-mono text-purple-400">{entry.referred_by}</span></span>
                  )}
                </p>
              </div>
              <div className="text-right hidden md:block">
                <div className="text-xs text-gray-500">Referrals</div>
                <div className={`text-lg font-bold ${(entry.referral_count || 0) >= threshold ? 'text-pink-300' : 'text-white'}`}>
                  {entry.referral_count || 0}<span className="text-xs text-gray-500">/{threshold}</span>
                </div>
              </div>
              <div className="text-right hidden sm:block min-w-[90px]">
                {entry.created_at && (
                  <p className="text-xs text-gray-500 flex items-center gap-1 justify-end">
                    <Calendar className="w-3 h-3" />
                    {new Date(entry.created_at).toLocaleDateString()}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDelete(entry.id, entry.email)}
                className="text-gray-500 hover:text-red-400 transition p-2"
                data-testid={`waitlist-delete-${entry.email}`}
                title="Remove"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------- Blast Tab ----------
const BlastTab = ({ count, authHeaders }) => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');
  const [testSent, setTestSent] = useState(false);
  const [sending, setSending] = useState(false);

  const canSend = subject.trim() && message.trim();

  const buildPayload = () => ({
    subject: subject.trim(),
    message: message.trim(),
    cta_label: ctaLabel.trim() || null,
    cta_url: ctaUrl.trim() || null,
  });

  const handleTest = async () => {
    if (!canSend) return toast.error('Subject and message are required');
    if (ctaLabel && !ctaUrl) return toast.error('Add a CTA URL or clear the label');
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/waitlist/blast/test`, buildPayload(), { headers: authHeaders() });
      toast.success(`Test sent to ${data.sent_to}`);
      setTestSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Test send failed');
    } finally {
      setSending(false);
    }
  };

  const handleBlast = async () => {
    if (!testSent) return toast.error('Send a test to yourself first');
    if (!window.confirm(`Send this blast to ${count} fans?`)) return;
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/admin/waitlist/blast`, buildPayload(), { headers: authHeaders() });
      toast.success(`Queued to ${data.queued} fans`);
      setSubject(''); setMessage(''); setCtaLabel(''); setCtaUrl(''); setTestSent(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Blast failed');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="glass-card p-6 space-y-4" data-testid="waitlist-blast-composer">
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Subject line</label>
        <input
          value={subject}
          onChange={(e) => { setSubject(e.target.value); setTestSent(false); }}
          className="input-dark w-full"
          placeholder="e.g. New track dropped tonight"
          data-testid="blast-subject-input"
          maxLength={200}
        />
      </div>
      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Message</label>
        <textarea
          value={message}
          onChange={(e) => { setMessage(e.target.value); setTestSent(false); }}
          rows={6}
          className="input-dark w-full font-normal resize-y"
          placeholder="Write what you want fans to know..."
          data-testid="blast-message-input"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Button label (optional)</label>
          <input
            value={ctaLabel}
            onChange={(e) => { setCtaLabel(e.target.value); setTestSent(false); }}
            className="input-dark w-full"
            placeholder="Listen Now"
            data-testid="blast-cta-label-input"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Button URL (optional)</label>
          <input
            value={ctaUrl}
            onChange={(e) => { setCtaUrl(e.target.value); setTestSent(false); }}
            className="input-dark w-full"
            placeholder="https://darealqjonesmusic.shop/music"
            data-testid="blast-cta-url-input"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-white/5">
        <button
          onClick={handleTest}
          disabled={!canSend || sending}
          className="btn-ghost flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="blast-test-button"
        >
          <TestTube2 className="w-4 h-4" /> Send test to me
        </button>
        <button
          onClick={handleBlast}
          disabled={!testSent || sending}
          className="btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
          data-testid="blast-send-button"
        >
          <Send className="w-4 h-4" /> Send to {count} fans
        </button>
      </div>
      {!testSent && canSend && (
        <p className="text-xs text-yellow-300/80" data-testid="blast-test-required-notice">
          Send a test to yourself first — the "Send to all" button unlocks after the test.
        </p>
      )}
    </div>
  );
};

// ---------- Bonus Tab ----------
const BonusTab = ({ threshold, authHeaders }) => {
  const [bonusUrl, setBonusUrl] = useState('');
  const [bonusTitle, setBonusTitle] = useState('Exclusive Bonus Track');
  const [bonusDescription, setBonusDescription] = useState(
    'Your unlocked reward for bringing 3 friends to the First-Listen list.'
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchBonus = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/waitlist/referral-bonus`, { headers: authHeaders() });
      const b = data.referral_bonus || {};
      if (b.bonus_url) setBonusUrl(b.bonus_url);
      if (b.bonus_title) setBonusTitle(b.bonus_title);
      if (b.bonus_description) setBonusDescription(b.bonus_description);
    } catch {
      /* first-time empty is fine */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchBonus(); }, []);

  const handleSave = async () => {
    if (!bonusUrl.trim()) return toast.error('Paste the download URL for the bonus file');
    setSaving(true);
    try {
      await axios.put(`${API_URL}/admin/waitlist/referral-bonus`, {
        bonus_url: bonusUrl.trim(),
        bonus_title: bonusTitle.trim(),
        bonus_description: bonusDescription.trim(),
      }, { headers: authHeaders() });
      toast.success('Referral bonus saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="glass-card p-8 text-center text-gray-400">Loading...</div>;

  return (
    <div className="glass-card p-6 space-y-4" data-testid="waitlist-bonus-config">
      <div className="flex items-start gap-3">
        <Gift className="w-6 h-6 text-pink-400 mt-1" />
        <div>
          <h3 className="font-semibold text-white">Referral Reward</h3>
          <p className="text-sm text-gray-400">
            Fans who bring <strong className="text-pink-300">{threshold}</strong> friends
            automatically get this file emailed to them.
          </p>
        </div>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">
          Bonus file URL (mp3 / zip / any downloadable link)
        </label>
        <input
          value={bonusUrl}
          onChange={(e) => setBonusUrl(e.target.value)}
          className="input-dark w-full font-mono text-sm"
          placeholder="https://storage.example.com/bonus-track.mp3"
          data-testid="bonus-url-input"
        />
        <p className="text-xs text-gray-500 mt-1">
          Tip: Upload the file under Admin → Files first, then paste its URL here.
        </p>
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Reward title</label>
        <input
          value={bonusTitle}
          onChange={(e) => setBonusTitle(e.target.value)}
          className="input-dark w-full"
          data-testid="bonus-title-input"
        />
      </div>

      <div>
        <label className="text-xs uppercase tracking-wide text-gray-400 mb-1 block">Description (shown in email)</label>
        <textarea
          value={bonusDescription}
          onChange={(e) => setBonusDescription(e.target.value)}
          rows={3}
          className="input-dark w-full resize-y"
          data-testid="bonus-description-input"
        />
      </div>

      <button
        onClick={handleSave}
        disabled={saving}
        className="btn-primary flex items-center gap-2 disabled:opacity-50"
        data-testid="bonus-save-button"
      >
        <Gift className="w-4 h-4" /> {saving ? 'Saving...' : 'Save Reward'}
      </button>
    </div>
  );
};
